import React, { useContext, useEffect } from 'react';
import cx from 'classnames';
import { kebabCase } from 'lodash';
import { useMutation } from 'react-query';
import { ErrorMessage } from '@hookform/error-message';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useHistory } from 'react-router';
import {
  deviceDetect,
  parseUserAgent,
  ReactDetectDeviceSpecs,
  ParsedUserAgent,
} from 'react-device-detect';

import Text from '~/App/Components/Text';
import { alertAsync } from '~/App/Components/Modals/Alert';
import { TextInputType } from '~/App/Components/Forms/TextInput';
import TestimonialContext from '../../Context/TestimonialContext';
import IntroCard from '~/App/Modules/Testimonials/Components/Common/IntroCard';
import {
  create as createRespondentApi,
  Respondent,
} from '~/Share/TestimonialLandingPage/Api/TestimonialRespondent';
import { AxiosError } from 'axios';
import useAnalytics from '~/react-hooks/use-analytics';
import { isEmail } from '~/utils/String';
import {
  TESTIMONIAL_LANDING_INTRO_LETS_GO,
  TESTIMONIAL_LANDING_VIEW,
} from '~/generated/AnalyticsTypes';
import { defaultTestimonialText } from '../../Pages/Editor/constants';

import { useTranslation } from 'react-i18next';
import { useFeatures } from '~/react-hooks/use-features';
import { getTestimonialLogoUrl } from '../../util';
import { getRichTextFromSlateData } from '@vimily/bonjoro-ui-components-v2/RichTextEditor';
import { computeTextColor } from '@vimily/bonjoro-ui-components-v2';
import { SentWithBonjoroBadgeStyle } from '@vimily/bonjoro-ui-components-v2/SentWithBonjoroBadge';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import styled from '@emotion/styled';
import { Input } from '@vimily/bonjoro-ui-components-v2/Input';
import { colors } from '@vimily/bonjoro-ui-components-v2/dist/theme';
import {
  addUtmToLink,
  UTM_CAMPAIGN_TESTIMONIALS_PLG,
  UTM_MEDIUM_POWERED_BY_BADGE,
  UTM_SOURCE_TESTIMONIAL_REQUEST_PAGE,
} from '~/utils/add-utm-to-link';
import { useMedia } from 'react-use-media';
import { BonjoroBadgeDesktop } from '~/App/Components/BonjoroBadgeDesktop';

export type IntroPageProps = {
  className?: string;
  preview?: boolean;
  fontFamily?: string;
};

export interface TestimonialRespondentMetaData {
  letsGo?: DeviceSpecs;
}

const StyledInput = styled(Input)<{ error?: boolean }>`
  border: 1px solid
    ${(props) => (props.error ? colors.red[700] : colors.neutral[900])};
`;

export default function IntroPage({ className, preview }: IntroPageProps) {
  const analytics = useAnalytics();

  const deviceDetected: ReactDetectDeviceSpecs = deviceDetect();

  const labelClassName = 'text-base font-bold mb-3 mr-1';
  const fieldErrorClassName = 'text-sm font-bold inline-block text-red-900';

  const contentClassName = [
    'flex flex-col justify-center items-center mx-auto pb-7',
    'w-[92%] max-w-[750px]',
  ].join(' ');

  const logoClassName = [
    'flex h-auto mx-auto mt-5 mb-7 w-auto max-h-[30px] max-w-[120px]',
    'md:my-16 md:max-h-[80px] md:max-w-[220px]',
  ].join(' ');

  const headerClassName = [
    'text-2xl mb-5 font-bold text-center w-full md:text-4xl',
  ].join(' ');

  const testimonial = useContext(TestimonialContext);
  const {
    uuid: testimonialUuid,
    name: testimonialName,
    user,
    text,
    metadata,
  } = testimonial;
  const logo = testimonial.landing_page.logo;
  const { shouldHideBonjoroTestimonialBranding } = useFeatures();
  const logoUrl = getTestimonialLogoUrl(
    shouldHideBonjoroTestimonialBranding,
    logo
  );
  const isMobile = useMedia({
    maxWidth: 767,
  });

  const {
    cta_corner_radius = 0,
    primary_color,
    text_color,
    font_family,
  } = metadata?.style ?? {};

  const history = useHistory();
  const { register, handleSubmit, errors } = useForm<Respondent>();

  const { t } = useTranslation();

  const errorAlert = (message?: string) =>
    alertAsync(
      <>
        <Text.SubHeading>
          {t('testimonial:validation.errorOccurred')}
        </Text.SubHeading>
        <Text.BodyCopy>
          {message} <br />
          {t('testimonial:validation.refresh')}
        </Text.BodyCopy>
      </>
    );

  const { mutateAsync: createRespondent, isLoading: loading } = useMutation(
    createRespondentApi,
    {
      useErrorBoundary: false,
      onError: (error: AxiosError) => {
        // rule: no-unsafe-member-access
        // eslint-disable-next-line
        errorAlert(error.response?.data?.error?.message as string);
      },
    }
  );

  // map the keys for consistency if mobile.  react-device-detect keys are inconsistent for mobile and desktop
  // for mobile and desktop userAgent keys are different
  const createFormattedDeviceDetect = () => {
    const parsedUserAgent: ParsedUserAgent = parseUserAgent(
      deviceDetected.userAgent ?? deviceDetected.ua
    ) as ParsedUserAgent;

    const formattedDeviceDetect = {
      isDesktop: deviceDetected.isBrowser ?? false,
      browserMajorVersion: parsedUserAgent.browser.major,
      browserFullVersion: parsedUserAgent.browser.version,
      browserName: parsedUserAgent.browser.name,
      engineName: parsedUserAgent.engine.name,
      engineVersion: parsedUserAgent.engine.version,
      osName: parsedUserAgent.os.name,
      osVersion: parsedUserAgent.os.version,
      userAgent: deviceDetected.userAgent,
      deviceVendor: parsedUserAgent.device.vendor,
      deviceModel: parsedUserAgent.device.model,
      deviceType: parsedUserAgent.device.type,
    };

    return formattedDeviceDetect;
  };

  const onSubmit: SubmitHandler<Respondent> = async (formData) => {
    if (preview) return;
    analytics.track(TESTIMONIAL_LANDING_INTRO_LETS_GO, formData);

    const res = await createRespondent({
      ...formData,
      testimonialUuid,
      metadata: JSON.stringify({ letsGo: createFormattedDeviceDetect() }),
    });
    if (res.data.uuid) {
      history.push(
        `/te/${kebabCase(
          testimonialName ?? 'testimonials'
        )}/${testimonialUuid}/${res.data.uuid}`
      );
    } else {
      // off chance that a success response was returned but has no uuid
      errorAlert('Unknown error');
    }
  };

  useEffect(() => {
    analytics.track(TESTIMONIAL_LANDING_VIEW, {
      testimonial_id: testimonialUuid,
      sender_email: user.email,
    });
  }, []);

  const buttonProps = {
    className:
      '!h-10 !py-1.5 w-full md:min-w-28 md:w-auto text-sm md:text-base',
    variant: ButtonVariant.Primary,
    disabled: loading,
    style: {
      borderRadius: `${cta_corner_radius}px`,
      borderColor: primary_color,
      backgroundColor: primary_color,
      color: computeTextColor(primary_color),
      fontFamily: font_family,
    },
  };

  return (
    <div
      className={cx(
        'flex flex-col relative w-full h-full m-auto pt-0',
        className
      )}
      style={{ fontFamily: font_family }}
    >
      <div className={contentClassName}>
        {!preview && (
          <img
            className={logoClassName}
            src={logoUrl ?? undefined}
            alt={logoUrl ?? undefined}
          />
        )}
        <IntroCard
          user={user}
          message={getRichTextFromSlateData(
            text.intro_rich ?? defaultTestimonialText.intro_rich
          )}
          className={cx({ 'md:mb-12': !preview })}
          fontColor={text_color}
          fontFamily={font_family}
        />
        <div className='w-full p-7 rounded'>
          <div
            className={cx(headerClassName, { 'md:mb-14': !preview })}
            style={{ color: text_color }}
          >
            {t('testimonial:intro.heading')}
          </div>
          <form
            className='w-full text-center'
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className='md:flex text-left'>
              <div className='md:w-1/2 flex-grow mb-5 md:mb-12 md:mr-5'>
                <label
                  className={labelClassName}
                  htmlFor='name'
                  style={{ color: text_color }}
                >
                  {t('testimonial:intro.name')}
                </label>
                <ErrorMessage
                  errors={errors}
                  name='name'
                  message={errors?.name?.message}
                  as={<div className={fieldErrorClassName} />}
                />
                <StyledInput
                  id='name'
                  name='name'
                  className='!v2-border-neutral-300 hover:!v2-border-neutral-300 focus:!v2-border-neutral-300'
                  error={!!errors.name}
                  type={TextInputType.Text}
                  placeholder={t('testimonial:intro.namePlaceholder')}
                  inputRef={register({
                    required: t('testimonial:validation.required').toString(),
                  })}
                />
              </div>
              <div className='md:w-1/2 flex-grow mb-5 md:mb-12'>
                <label
                  className={labelClassName}
                  htmlFor='email'
                  style={{ color: text_color }}
                >
                  {t('testimonial:intro.email')}
                </label>
                <ErrorMessage
                  errors={errors}
                  message={errors?.email?.message}
                  name='email'
                  as={<div className={fieldErrorClassName} />}
                />
                <StyledInput
                  id='email'
                  name='email'
                  className='text-base bold !v2-border-neutral-300 hover:!v2-border-neutral-300 focus:!v2-border-neutral-300'
                  type={TextInputType.Email}
                  error={!!errors.email}
                  placeholder={t('testimonial:intro.emailPlaceholder')}
                  inputRef={register({
                    required: t('testimonial:validation.required').toString(),
                    validate: isEmail,
                  })}
                />
              </div>
            </div>
            <Button {...buttonProps}>
              {t('testimonial:intro.action')}
              {loading && <i className='far fa-spinner fa-spin ml-1 left-2' />}
            </Button>
          </form>
        </div>
      </div>
      {!shouldHideBonjoroTestimonialBranding && (
        <BonjoroBadgeDesktop
          style={
            isMobile
              ? SentWithBonjoroBadgeStyle.Compact
              : SentWithBonjoroBadgeStyle.Full
          }
          url={addUtmToLink('/testimonials', {
            utm_source: UTM_SOURCE_TESTIMONIAL_REQUEST_PAGE,
            utm_medium: UTM_MEDIUM_POWERED_BY_BADGE,
            utm_campaign: UTM_CAMPAIGN_TESTIMONIALS_PLG,
          })}
        />
      )}
    </div>
  );
}
